import React, { useState, useEffect } from 'react';
import { attendeesAPI } from '../services/api';

const PhotoModal = ({ show, attendee, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('imagen');
  const [attendeeDetails, setAttendeeDetails] = useState(null);

  useEffect(() => {
    if (show && attendee && attendee.id && attendee.hasFacialRecognition) {
      loadAttendeeDetails();
    }
  }, [show, attendee]);

  const loadAttendeeDetails = async () => {
    if (!attendee || !attendee.id) return;
    
    setLoadingDetails(true);
    try {
      const response = await attendeesAPI.getAttendeeDetails(attendee.id);
      if (response.data.success) {
        setAttendeeDetails(response.data.attendee);
      }
    } catch (error) {
      console.error('Error cargando detalles del asistente:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  if (!show) return null;

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleDeletePhoto = async () => {
    if (!window.confirm(`¿Eliminar los datos de reconocimiento facial de ${attendee.fullName}?\n\nEsta acción no se puede deshacer.`)) {
      return;
    }

    setLoading(true);
    try {
      await attendeesAPI.deletePhoto(attendee.id);
      showMessage('success', `Datos de reconocimiento facial de "${attendee.fullName}" eliminados`);
      onClose();
    } catch (error) {
      showMessage('error', error.response?.data?.error || 'Error eliminando datos biométricos');
    } finally {
      setLoading(false);
    }
  };

  const hasPhotoUrl = attendee.photoUrl;
  const hasFacialRecognition = attendee.hasFacialRecognition;
  const facialImageUrl = attendee.facialImageUrl || attendee.photoUrl;
  const descriptorLength = attendeeDetails?.facialDescriptorsLength || attendee.facialDescriptorsLength || 0;
  const facialDescriptor = attendeeDetails?.facialDescriptor;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold">Datos Biométricos</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Mensaje de estado */}
        {message.text && (
          <div className={`m-4 p-4 rounded ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {message.text}
          </div>
        )}

        {/* Body */}
        <div className="px-6 py-6 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
          {/* Información del asistente */}
          <div className="space-y-2 text-center bg-gray-50 p-4 rounded-lg">
            <p className="font-medium text-gray-900 text-lg">{attendee.fullName}</p>
            {attendee.email && <p className="text-sm text-gray-600">{attendee.email}</p>}
            {attendee.company && <p className="text-sm text-gray-600">{attendee.company}</p>}
          </div>

          {/* Estados de datos biométricos */}
          <div className="flex justify-center space-x-4">
            <div className={`px-4 py-2 rounded-lg ${hasPhotoUrl ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
              <span className="text-xl mr-2">📷</span>
              Foto: {hasPhotoUrl ? 'Sí' : 'No'}
            </div>
            <div className={`px-4 py-2 rounded-lg ${hasFacialRecognition ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>
              <span className="text-xl mr-2">🔐</span>
              Facial: {hasFacialRecognition ? 'Sí' : 'No'}
            </div>
          </div>

          {/* Tabs para navegación */}
          {(hasFacialRecognition || hasPhotoUrl) && (
            <div className="flex border-b border-gray-200">
              {hasPhotoUrl && (
                <button
                  type="button"
                  onClick={() => setActiveTab('imagen')}
                  className={`px-4 py-2 text-sm font-medium ${
                    activeTab === 'imagen'
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  📷 Imagen Capturada
                </button>
              )}
              {hasFacialRecognition && (
                <button
                  type="button"
                  onClick={() => setActiveTab('frames')}
                  className={`px-4 py-2 text-sm font-medium ${
                    activeTab === 'frames'
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  📊 Datos Técnicos
                </button>
              )}
              {hasFacialRecognition && (
                <button
                  type="button"
                  onClick={() => setActiveTab('poses')}
                  className={`px-4 py-2 text-sm font-medium ${
                    activeTab === 'poses'
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  🔄 Frames Capturados
                </button>
              )}
            </div>
          )}

          {/* Contenido de tabs */}
          {activeTab === 'imagen' && (
            <div className="space-y-4">
              <div className="relative">
                {facialImageUrl ? (
                  <div className="text-center">
                    <img
                      src={facialImageUrl}
                      alt={`Foto facial de ${attendee.fullName}`}
                      className="max-w-full h-auto rounded-lg border border-gray-300 shadow-lg mx-auto"
                      style={{ maxHeight: '350px', objectFit: 'contain' }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden w-full h-64 flex-col items-center justify-center bg-gray-100 rounded-lg border border-dashed border-gray-400 text-gray-500">
                      <svg className="w-12 h-12 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p>Imagen no disponible</p>
                      <p className="text-xs text-gray-400 mt-1">URL: {facialImageUrl}</p>
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-64 flex items-center justify-center bg-gray-100 rounded-lg border border-dashed border-gray-400">
                    <div className="text-center text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 8a3 3 0 11-6 0 3 3 0 016 0zM5.303 5.303L3 3m10.597 10.597l2.121 2.121M9 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <p>No tiene imagen facial guardada</p>
                    </div>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-500 text-center">
                Imagen capturada durante el proceso de registro con detección de vida (liveness)
              </p>
            </div>
          )}

          {activeTab === 'frames' && hasFacialRecognition && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-3">🔐 Información del Descriptor Facial</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">Longitud del Embedding</p>
                    <p className="text-lg font-mono font-bold text-blue-600">{descriptorLength} valores</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">Tipo de Descriptor</p>
                    <p className="text-lg font-semibold text-green-600">{descriptorLength >= 512 ? 'ONNX (ArcFace)' : 'face-api.js'}</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">Motor de Detección</p>
                    <p className="text-sm font-semibold text-purple-600">{descriptorLength >= 512 ? 'SCRFD 500M' : 'SSD MobileNet'}</p>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <p className="text-xs text-gray-500">Dimensionalidad</p>
                    <p className="text-sm font-mono">{descriptorLength}-dimensional</p>
                  </div>
                </div>
              </div>

              {/* Desplegable con datos crudos del descriptor */}
              <details className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                <summary className="px-4 py-3 bg-gray-100 cursor-pointer hover:bg-gray-200 font-medium text-gray-700 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-gray-500 transform transition-transform" style={{}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  📋 Ver Datos Crudos (facial_descriptors)
                </summary>
                <div className="p-4 bg-white border-t border-gray-200">
                  {loadingDetails ? (
                    <div className="flex items-center justify-center py-8">
                      <svg className="animate-spin h-8 w-8 text-blue-500" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="ml-3 text-gray-500">Cargando datos...</span>
                    </div>
                  ) : facialDescriptor ? (
                    <>
                      <p className="text-xs text-gray-500 mb-2">
                        Este es el vector de {descriptorLength} valores que representa las características faciales únicas.
                        Se usa para comparar rostros durante el check-in facial.
                      </p>
                      <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-xs overflow-x-auto max-h-64 overflow-y-auto">
                        <pre className="whitespace-pre">
{JSON.stringify({
  type: 'facial_descriptor',
  length: descriptorLength,
  engine: descriptorLength >= 512 ? 'ONNX (ArcFace)' : 'face-api.js',
  values: facialDescriptor
}, null, 2)}
                        </pre>
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        ⚠️ Estos valores son confidenciales y representan los datos biométricos del usuario.
                      </p>
                    </>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      <p>No se pudieron cargar los datos del descriptor facial.</p>
                      <button
                        type="button"
                        onClick={loadAttendeeDetails}
                        className="mt-2 text-blue-600 hover:text-blue-800 text-sm underline"
                      >
                        Reintentar
                      </button>
                    </div>
                  )}
                </div>
              </details>

              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-yellow-800">Información Técnica</h4>
                    <p className="mt-1 text-sm text-yellow-700">
                      El descriptor facial es un vector numérico que representa las características únicas del rostro.
                      {descriptorLength >= 512 ? (
                        <> Se usa el modelo ArcFace que proporciona alta precisión en reconocimiento facial.</>
                      ) : (
                        <> Se usa el modelo de face-api.js que proporciona 128 valores característicos.</>
                      )}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="text-sm font-medium text-blue-800 mb-2">🔒 Proceso de Captura</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>✓ Detección de 468 puntos faciales (MediaPipe)</li>
                  <li>✓ Verificación de pose (5 posiciones)</li>
                  <li>✓ Validación anti-spoofing</li>
                  <li>✓ Extracción de embeddings faciales</li>
                  <li>✓ Normalización L2 del vector</li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'poses' && hasFacialRecognition && (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800">🔄 Posiciones Capturadas durante Liveness Detection</h3>
              <div className="grid grid-cols-5 gap-2">
                {[
                  { id: 'center', label: 'Frente', icon: '👤', emoji: 'Mira al frente' },
                  { id: 'left', label: 'Izquierda', icon: '👈', emoji: 'Gira a tu izquierda' },
                  { id: 'right', label: 'Derecha', icon: '👉', emoji: 'Gira a tu derecha' },
                  { id: 'up', label: 'Arriba', icon: '👆', emoji: 'Mira arriba' },
                  { id: 'down', label: 'Abajo', icon: '👇', emoji: 'Mira abajo' },
                ].map((pose) => (
                  <div key={pose.id} className="bg-gray-50 p-3 rounded-lg text-center border">
                    <div className="text-2xl mb-1">{pose.icon}</div>
                    <p className="text-xs font-medium text-gray-700">{pose.label}</p>
                    <p className="text-xs text-gray-400">{pose.emoji}</p>
                  </div>
                ))}
              </div>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-green-800">Validación Exitosa</h4>
                    <p className="mt-1 text-sm text-green-700">
                      Se capturaron múltiples poses para garantizar que el usuario es una persona real y no un intento de spoofing (fotos, pantallas, máscaras).
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 mb-2">¿Cómo funciona?</h4>
                <ol className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start">
                    <span className="bg-blue-100 text-blue-600 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">1</span>
                    <span>Se muestran instrucciones para mirar en diferentes direcciones</span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-blue-100 text-blue-600 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">2</span>
                    <span>MediaPipe detecta 468 puntos faciales en tiempo real</span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-blue-100 text-blue-600 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">3</span>
                    <span>Se validan los ángulos de orientación de la cabeza</span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-blue-100 text-blue-600 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">4</span>
                    <span>Cuando la pose es correcta, se captura un frame</span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-blue-100 text-blue-600 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">5</span>
                    <span>Se extraen los descriptores faciales de cada frame</span>
                  </li>
                  <li className="flex items-start">
                    <span className="bg-blue-100 text-blue-600 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold mr-2 flex-shrink-0">6</span>
                    <span>Se combinan los embeddings y se guardan en la base de datos</span>
                  </li>
                </ol>
              </div>
            </div>
          )}

          {/* Mensaje cuando no hay datos biométricos */}
          {!hasPhotoUrl && !hasFacialRecognition && (
            <div className="w-full h-64 flex items-center justify-center bg-gray-100 rounded-lg border border-dashed border-gray-400">
              <div className="text-center text-gray-500">
                <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 8a3 3 0 11-6 0 3 3 0 016 0zM5.303 5.303L3 3m10.597 10.597l2.121 2.121M9 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="text-lg font-medium">Sin datos biométricos</p>
                <p className="text-sm text-gray-400 mt-1">Este asistente no tiene reconocimiento facial registrado</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div>
            {(hasPhotoUrl || hasFacialRecognition) && (
              <button type="button" onClick={handleDeletePhoto} disabled={loading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition disabled:opacity-50"
              >
                {loading ? 'Eliminando...' : '🗑️ Eliminar Datos Biométricos'}
              </button>
            )}
          </div>
          <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhotoModal;